import type { AppContextType } from "@1js/acui-context";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { memoizeFunction } from "@fluentui/react";

import type { EstimatedRibbonWidth } from ".";
import {
  getAdjacentControlProps,
  getControlDimensionsForRibbonModeById,
  getControlGroupFromControlId,
  getControlGroupFromGroupId,
  getControlProps,
  getCoreRibbonElement,
  getElementWidth,
  getPreviewGalleryGhostFlyout,
  getRibbonElementTextContainer,
  getRibbonPreviewGalleryItems,
} from ".";
import type {
  BuildTimeControlWidths,
  EstimatedScalingWidthDict,
  RibbonScalingStateSLRWithCacheKeys,
  VirtualRibbonScalingStateSLR,
} from "..";
import { ControlState } from "..";
import { getOffsetForSpecificControlType } from "../../../Controls";
import type {
  RibbonScalingStep,
  RibbonScalingTransformation,
} from "../../../Scaling";
import { overflowButtonWidth } from "../../RibbonControlGroupSLR";
import { getVerticalDividerWidthSLR } from "../RibbonBottomBarConstants";

/**
 * Initializes the ribbon's VirtualRibbonScalingState
 *
 * @param scalingState The initial scalingState used to populate the virtual scaling state
 */
export function initializeVirtualScalingStateSLR(
  scalingState: RibbonScalingStateSLRWithCacheKeys,
  knownWidths?: BuildTimeControlWidths
): VirtualRibbonScalingStateSLR {
  const virtualScalingState: VirtualRibbonScalingStateSLR = {
    controls: {},
    groups: {},
    containsSingleOverflow: !!(
      scalingState.scaledEndOfRibbonOverflowMenu?.sections?.length > 0
    ),
  };

  scalingState.scaledRibbonGroups.forEach((group) => {
    virtualScalingState.groups[group.id] = {
      topLevelControlCount: group.topLevelControls.length,
      containsOverflow: group.overflowControls.length > 0,
    };
  });

  if (knownWidths) {
    for (const id in knownWidths) {
      if (controlIsInRibbonDefinitionSLR(id, scalingState)) {
        const previewGalleryItems = knownWidths[id].previewGalleryItems;
        virtualScalingState.controls[id] = {
          fullControlWidth: knownWidths[id].componentRoot,
          labelWidth: knownWidths[id].textContainer,
          previewGalleryItemWidths: previewGalleryItems
            ? [...previewGalleryItems]
            : undefined,
          ghostFlyout: knownWidths[id].ghostFlyout,
          ghostFlyoutTextContainer: knownWidths[id].ghostFlyoutTextContainer,
        };
      }
    }
  }

  return virtualScalingState;
}

/**
 * Estimates the impact of applying every scaling step on the current tab, and fills the estimatedWidths dict with these values
 *
 * @param scalingState The current scaling state for the ribbon. This holds all data necessary to render and transform the ribbon
 * @param measuredDimension The width of the ribbon with only the initial scaling steps applied. This is the base value we will count down from
 */
export const estimateRibbonScalingStepsSLR = memoizeFunction(
  (
    scalingState: RibbonScalingStateSLRWithCacheKeys,
    measuredDimension: number,
    appContext: AppContextType,
    draftVirtualScalingState?: VirtualRibbonScalingStateSLR
  ) => {
    const estimatedWidths: EstimatedScalingWidthDict = {};
    let nextScalingStepIndex = scalingState.appliedScalingStepCount;

    // The measured dimension will be the width of the applied scaling step
    estimatedWidths[nextScalingStepIndex] = measuredDimension;
    nextScalingStepIndex = nextScalingStepIndex + 1;

    let currentWidth = measuredDimension;
    if (!draftVirtualScalingState) {
      // tslint:disable: no-parameter-reassignment
      /* eslint-disable no-param-reassign */
      draftVirtualScalingState = initializeVirtualScalingStateSLR(scalingState);
      /* eslint-enable no-param-reassign */
      // tslint:enable: no-parameter-reassignment
    }

    while (nextScalingStepIndex < scalingState.scalingSteps.length) {
      currentWidth -= calculatePixelsSavedFromScalingStepSLR(
        scalingState,
        // Use the width of one scaling step prior because that will
        // be the max width of the next scaling step
        scalingState.scalingSteps[nextScalingStepIndex - 1],
        draftVirtualScalingState,
        appContext
      );

      estimatedWidths[nextScalingStepIndex] = currentWidth;
      nextScalingStepIndex += 1;
    }

    return estimatedWidths;
  }
);

/**
 * Estimates the number of pixels saved by applying a given scaling step to the ribbon
 *
 * @param scalingState The current scaling state for the ribbon. This holds all data necessary to render and transform the ribbon
 * @param scalingStep The scaling step that is being applied
 * @param virtualScalingState Information used to keep track of virtual changes to the DOM caused by previously estimated scaling steps
 * @returns The number of pixels by which the ribbon contents will shrink after applying the given scaling step
 */
function calculatePixelsSavedFromScalingStepSLR(
  scalingState: RibbonScalingStateSLRWithCacheKeys,
  scalingStep: Readonly<RibbonScalingStep>,
  draftVirtualScalingState: VirtualRibbonScalingStateSLR,
  appContext: AppContextType
): number {
  if (Array.isArray(scalingStep)) {
    return scalingStep.reduce(
      (accumulator, scalingStep) =>
        accumulator +
        calculatePixelsSavedFromScalingStepSLR(
          scalingState,
          scalingStep,
          draftVirtualScalingState,
          appContext
        ),
      0
    );
  }
  const step = scalingStep as RibbonScalingTransformation;
  const coreRibbonElement = getCoreRibbonElement(step);

  switch (step.type) {
    case "DropLabel":
      return pixelsSavedFromDropLabelStep(
        step.controlId,
        draftVirtualScalingState,
        coreRibbonElement
      );
    case "MoveToOverflow":
      return pixelsSavedFromMoveToOverflowStep(
        step.controlId,
        draftVirtualScalingState,
        scalingState,
        coreRibbonElement
      );
    case "DropPreviewItemFromGallery":
      return pixelsSavedFromDropPreviewItemFromGalleryStep(
        step.controlId,
        draftVirtualScalingState,
        coreRibbonElement
      );
    case "GalleryToFlyout":
      return pixelsSavedFromGalleryToFlyoutStep(
        step.controlId,
        draftVirtualScalingState,
        coreRibbonElement
      );
    case "MoveGroupToSingleOverflow":
      return pixelsSavedFromMoveGroupToSingleOverflowStep(
        step.groupId,
        draftVirtualScalingState,
        scalingState
      );
  }

  return 0;
}

/**
 * The 'DropLabel' scaling step will result in the removal of a control's label. The effect of this step can be
 * estimated by determining and returning the width of the label's textContainer
 */
function pixelsSavedFromDropLabelStep(
  controlId: string,
  draftVirtualScalingState: VirtualRibbonScalingStateSLR,
  element: Element | null
) {
  /* If there's already an entry for this control in the virtualScalingState, then this is more up-to-date than what's in the DOM*/
  const draftControl = draftVirtualScalingState.controls[controlId];

  const fullControlWidth =
    draftControl?.fullControlWidth ?? getElementWidth(element);
  const controlLabelWidth =
    draftControl?.labelWidth ??
    getElementWidth(getRibbonElementTextContainer(element));

  /* If we can't find the control, this scaling step will have no effect */
  if (!fullControlWidth || !controlLabelWidth) {
    return 0;
  }

  /* Update the virtualScalingState with the new state of the ribbon */
  draftVirtualScalingState.controls[controlId] = {
    ...draftControl,
    fullControlWidth: fullControlWidth - controlLabelWidth,
    labelWidth: 0,
    controlState: ControlState.LabelDropped,
  };

  return controlLabelWidth;
}

function calculatePixelsForControl(controlId: string) {
  return getControlDimensionsForRibbonModeById(controlId, "SingleLine");
}

/**
 * The 'MoveToOverflow' scaling step will remove the control from the top level of the ribbon and put it inside an overflow menu.
 * Depending on the current state of the control group, this may also result in the creation of a new overflow menu control either
 * at the end of the current control group, or in a new control group at the end of the ribbon.
 *
 * The effect of this step can be estimated by determining the width of the control being dropped and adding that to the width of
 * the overflow button being added, if necessary.
 */
function pixelsSavedFromMoveToOverflowStep(
  controlId: string,
  draftVirtualScalingState: VirtualRibbonScalingStateSLR,
  scalingState: RibbonScalingStateSLRWithCacheKeys,
  element: Element | null
) {
  let controlWidth =
    draftVirtualScalingState.controls[controlId]?.fullControlWidth ??
    getElementWidth(element);
  const controlGroup = getControlGroupFromControlId(scalingState, controlId);

  /* If we can't find the control, this scaling step will have no effect */
  if (!controlWidth || !controlGroup) {
    return 0;
  }

  controlWidth += getOffsetForSpecificControlType(
    getControlProps(controlGroup, controlId),
    getAdjacentControlProps(controlGroup, controlId)
  );

  /* There are a few special cases to consider when dropping the last element in a controlGroup */
  if (
    draftVirtualScalingState.groups[controlGroup.id]?.topLevelControlCount === 1
  ) {
    /* Some control groups specify that they should not allow all control to be dropped. In this case, the scaling step will no-op
       This does not apply to controls that were always in the group by themselves */
    if (
      !controlGroup.shouldAllowAllControlsInOverflow &&
      controlGroup.topLevelControls.length > 1
    ) {
      return 0;
    }
    /* When removing the last control from a control group, you need to account for the divider disappearing */
    controlWidth += getVerticalDividerWidthSLR();
  }

  /* If the overflow for this control already exists, we don't need to account for its creation */
  const overflowAlreadyExists =
    draftVirtualScalingState.groups[controlGroup.id]?.containsOverflow ||
    (!controlGroup.hasOwnOverflow &&
      draftVirtualScalingState.containsSingleOverflow);

  /* If this scaling step will result in a new overflow at the end of the ribbon, we need to account for the width of the new control group */
  const sizeOfNewOverflow = controlGroup.hasOwnOverflow
    ? overflowButtonWidth
    : overflowButtonWidth + getVerticalDividerWidthSLR();

  /* Update the virtualScalingState with the new state of the ribbon */
  if (!controlGroup.hasOwnOverflow) {
    draftVirtualScalingState.containsSingleOverflow = true;
  }

  draftVirtualScalingState.controls[controlId] = {
    controlState: ControlState.MovedToOverflow,
    fullControlWidth: 0,
    labelWidth: 0,
  };

  draftVirtualScalingState.groups[controlGroup.id] = {
    containsOverflow: true,
    topLevelControlCount:
      draftVirtualScalingState.groups[controlGroup.id].topLevelControlCount - 1,
  };

  return controlWidth - (overflowAlreadyExists ? 0 : sizeOfNewOverflow);
}

/**
 * The 'DropPreviewItemFromGallery' scaling step will remove the last visible item from the preview gallery. Since preview gallery
 * items can each be rendered with a different size, it is important to measure the specific button being dropped. The effect of this
 * step can be estimated by determining which button is being dropped and returning its width
 */
function pixelsSavedFromDropPreviewItemFromGalleryStep(
  controlId: string,
  draftVirtualScalingState: VirtualRibbonScalingStateSLR,
  element: Element | null
) {
  const previewGalleryWidth =
    draftVirtualScalingState.controls[controlId]?.fullControlWidth ??
    getElementWidth(element);

  /* calculate the width of each gallery button if we haven't already done so */
  if (!draftVirtualScalingState.controls[controlId]?.previewGalleryItemWidths) {
    const galleryButtonElements = getRibbonPreviewGalleryItems(element) ?? [];
    const galleryButtonWidths = [];
    for (let i = 0; i < galleryButtonElements.length; i += 1) {
      galleryButtonWidths.push(getElementWidth(galleryButtonElements[i]) || 0);
    }
    draftVirtualScalingState.controls[controlId] = {
      ...draftVirtualScalingState.controls[controlId],
      previewGalleryItemWidths: galleryButtonWidths,
    };
  }

  const galleryButtonWidth =
    draftVirtualScalingState.controls[
      controlId
    ].previewGalleryItemWidths?.pop();

  /* If we can't find the control, this scaling step will have no effect */
  if (!previewGalleryWidth || !galleryButtonWidth) {
    return 0;
  }

  /* Update the virtualScalingState with the new state of the ribbon */
  draftVirtualScalingState.controls[controlId] = {
    ...draftVirtualScalingState.controls[controlId],
    fullControlWidth: previewGalleryWidth - galleryButtonWidth,
    labelWidth: 0,
    controlState: ControlState.PartialGallery,
  };

  return galleryButtonWidth;
}

/**
 * The 'GalleryToFlyout' scaling step will convert the specified PreviewGallery into a FlyoutAnchor. The effect of this step can
 * can be estimated by returning the difference in width between the outgoing PreviewGallery and the incoming FlyoutAnchor. The
 * FlyoutAnchor needs to have been rendered hidden so that its width can be accurately estimated.
 */
function pixelsSavedFromGalleryToFlyoutStep(
  controlId: string,
  draftVirtualScalingState: VirtualRibbonScalingStateSLR,
  element: Element | null
) {
  const draftControl = draftVirtualScalingState.controls[controlId];
  const galleryWidth =
    draftControl?.fullControlWidth ?? getElementWidth(element);
  const ghostFlyout = getPreviewGalleryGhostFlyout(element, controlId);
  const galleryFlyoutWidth =
    draftControl?.ghostFlyout ?? getElementWidth(ghostFlyout);
  const galleryLabel = getRibbonElementTextContainer(ghostFlyout);
  const ghostLabelWidth =
    draftControl?.ghostFlyoutTextContainer ?? getElementWidth(galleryLabel);

  /* If we can't find the control, this scaling step will have no effect */
  if (
    !galleryWidth ||
    !galleryFlyoutWidth ||
    !ghostLabelWidth ||
    draftControl?.previewGalleryItemWidths?.length === 0
  ) {
    return 0;
  }

  /* Update the virtualScalingState with the new state of the ribbon */
  draftVirtualScalingState.controls[controlId] = {
    fullControlWidth: galleryFlyoutWidth,
    labelWidth: ghostLabelWidth,
    controlState: ControlState.FullControl,
    previewGalleryItemWidths: [],
  };

  return galleryWidth - galleryFlyoutWidth;
}

function pixelsSavedFromMoveGroupToSingleOverflowStep(
  groupId: string,
  draftVirtualScalingState: VirtualRibbonScalingStateSLR,
  scalingState: RibbonScalingStateSLRWithCacheKeys
) {
  let spaceSaved = 0;
  const controlGroup = getControlGroupFromGroupId(scalingState, groupId);
  /* The current overflow for this group is being merged with the single overflow at the end of the ribbon */
  if (controlGroup && controlGroup.overflowControls.length > 0) {
    spaceSaved += overflowButtonWidth;
  }

  /* If there isn't already a single overflow, this scaling step will create it */
  if (!draftVirtualScalingState.containsSingleOverflow) {
    spaceSaved -= overflowButtonWidth + getVerticalDividerWidthSLR();
  }

  /* Update the virtualScalingState with the new state of the ribbon */
  draftVirtualScalingState.containsSingleOverflow = true;

  return spaceSaved;
}

/**
 * Estimates the width of the bottom bar based on known control dimensions determined at build-time
 */
export function getRibbonBottomBarWidthSLR(
  scalingState: RibbonScalingStateSLRWithCacheKeys,
  knownWidths: BuildTimeControlWidths,
  additionalTotalWidth?: number,
  reactMLRIsEnabled?: boolean,
  ensureFullWidths?: boolean
): EstimatedRibbonWidth {
  const unknownIds: string[] = [];
  let totalIdCount: number = 0;
  let ribbonWidth = scalingState.scaledRibbonGroups.reduce(
    (accumulator, group, index) => {
      let widthOfGroup = 0;
      // Add up the width of every top level control in the group
      group.topLevelControls.forEach((control) => {
        totalIdCount = totalIdCount + 1;

        const isMissingRootWidth = !knownWidths[control.id]?.componentRoot;

        // Check if we need to get the text width.
        //
        // Note, we could be missing the text width if a control renders on the initial tab with hideLabel
        // in the initialScalingSteps as well as rendering with the label in a different tab
        const needsTextWidth =
          ensureFullWidths &&
          !knownWidths[control.id]?.textContainer &&
          !knownWidths[control.id]?.previewGalleryItems;

        // We need to measure if either we do not have a root width or
        // we need to get the text width
        const needsMeasurement = isMissingRootWidth || needsTextWidth;

        // If need to measure, attempt to calculate it
        if (needsMeasurement) {
          const controlDimensions = calculatePixelsForControl(control.id);

          // If we got valid controlDimensions add them to the known widths
          if (controlDimensions?.componentRoot) {
            knownWidths[control.id] = controlDimensions;
          }
        }

        // If we have a valid known width, add it to the width of the group
        if (knownWidths[control.id]?.componentRoot) {
          widthOfGroup += knownWidths[control.id].componentRoot;
        } else {
          unknownIds.push(control.id);
        }
      });

      // If there is an overflow well, account for it
      if (group.hasOwnOverflow && group.overflowControls.length > 0) {
        widthOfGroup += overflowButtonWidth;
      }
      // The last group only contains a divider if the MLR is enabled
      const isFinalGroup = index === scalingState.scaledRibbonGroups.length - 1;
      return (
        accumulator +
        widthOfGroup +
        (isFinalGroup && !reactMLRIsEnabled ? 0 : getVerticalDividerWidthSLR())
      );
    },
    0
  );

  if (scalingState.scaledEndOfRibbonOverflowMenu?.sections.length > 0) {
    ribbonWidth += overflowButtonWidth + getVerticalDividerWidthSLR();
  }

  return {
    width: ribbonWidth + (additionalTotalWidth ?? 0),
    unknownIds,
    totalIdCount,
  };
}

/**
 * Returns true if a given controlId is in the current ribbon definition. This is used to
 * filter out controls that may have been trimmed at boot. In these scenarios, we need to know
 * that a scaling step on these controls has no effect
 */
function controlIsInRibbonDefinitionSLR(
  controlId: string,
  scalingState: RibbonScalingStateSLRWithCacheKeys
) {
  return scalingState.scaledRibbonGroups.some((group) => {
    return group.topLevelControls.some((control) => {
      return control.id === controlId;
    });
  });
}
